import { rasterSrc } from "@/generated/raster-images";
import { createFileRoute } from "@tanstack/react-router";
import { ExternalLink, Search, Wrench, ShieldCheck, Sparkles } from "lucide-react";
import { PageHero, Section, SectionHeader, CTABand } from "@/components/site-blocks";
import { EnquiryForm } from "@/components/enquiry-form";
import { CHECK_BRANDS, type CheckBrand } from "@/lib/services";
import { pageSeo } from "@/lib/seo";

export const Route = createFileRoute("/the-home-check-group")({
  head: () => ({
    ...pageSeo({
      path: "/the-home-check-group",
      title: "The Home Check Group: Diagnostics & Remediation | CRS Energy",
      description:
        "Four diagnostic brands: Home Damp, Insulation, Renewables and Grant Check, all delivered by CRS, the contractor behind every survey.",
    }),
  }),
  component: HomeCheckGroupPage,
});

const BRAND_DETAIL: Record<
  CheckBrand,
  {
    intro: string;
    services: string[];
    bestFor: string;
  }
> = {
  HDC: {
    intro:
      "Home Damp Check is the diagnostic-led survey brand for damp, mould and condensation. Independent surveyors investigate the root cause and produce a clear, photo-rich report: no upselling, no guesswork.",
    services: [
      "Rising damp, penetrating damp & condensation surveys",
      "Mould investigations & air-quality assessments",
      "Independent reports for homeowners, landlords and insurers",
    ],
    bestFor: "Homeowners and landlords seeing damp patches, mould, salt staining or musty smells.",
  },
  HIC: {
    intro:
      "Home Insulation Check audits the insulation already in your home: cavity walls, lofts, floors and rooms in roof, and identifies what's underperforming, saturated or missing entirely.",
    services: [
      "Cavity wall insulation inspections (boroscope surveys)",
      "Loft, floor and room-in-roof insulation assessments",
      "Energy-loss reports & retrofit recommendations",
    ],
    bestFor: "Homes with high energy bills, cold spots, or properties where old insulation may have failed.",
  },
  HRC: {
    intro:
      "Home Renewables Check is the impartial survey brand for low-carbon heating and renewables: heat pumps, solar PV, battery storage and hot-water upgrades: sized properly for your home.",
    services: [
      "Heat pump suitability & heat-loss surveys",
      "Solar PV and battery feasibility assessments",
      "Hot water cylinder & smart heating upgrades",
    ],
    bestFor: "Homeowners considering renewables, replacing an old boiler, or planning a future-proof retrofit.",
  },
  HGC: {
    intro:
      "Home Grant Check helps homeowners and private tenants find out if they qualify for fully-funded energy efficiency measures under the Government's ECO4 scheme and the Boiler Upgrade Scheme, including A-rated boilers, heat pumps, solar PV, cavity wall and loft insulation. The eligibility check is free, and a home survey is arranged with no obligation.",
    services: [
      "Boiler Upgrade Scheme Eligibility",
      "Free home survey to confirm qualifying measures",
      "End-to-end installation through accredited partners",
    ],
    bestFor: "Finding out if you're eligible for grants.",
  },
};

const ORDER: CheckBrand[] = ["HDC", "HIC", "HRC", "HGC"];

function HomeCheckGroupPage() {
  return (
    <>
      <PageHero
        eyebrow="The Home Check Group"
        title="Four diagnostic brands. One trusted contractor."
        description="The Home Check Group exists to separate honest diagnosis from delivery, so homeowners get the right survey, the right advice, and the right work without the conflict of interest that sits at the heart of so much of our industry."
        image={rasterSrc("stock/photo-1560185007-cde436f6a4d0")}
        imageAlt="Surveyor inspecting a home"
      />

      <Section>
        <div className="grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <SectionHeader
              eyebrow="Why we work this way"
              title="The diagnostic comes first. Always."
            />
            <div className="space-y-4 text-muted-foreground text-pretty">
              <p>
                When remediation work <em>is</em> needed, many choose CRS because we sit behind the group as a fully accredited delivery partner with 50+ years of combined experience. The survey stands on its own: independent, transparent, and yours to act on.
              </p>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {[
              {
                icon: Search,
                title: "Independent diagnosis",
                body: "Each Check brand surveys with independent surveyors.",
              },
              {
                icon: ShieldCheck,
                title: "No conflict of interest",
                body: "Surveyors aren't paid on conversion. They're paid to find the truth.",
              },
              {
                icon: Wrench,
                title: "One contractor, every measure",
                body: "If you choose to proceed, CRS can deliver insulation, damp, structural, heating and renewables in one coordinated programme.",
              },
              {
                icon: Sparkles,
                title: "Joined-up retrofit",
                body: "Surveys talk to each other, so your damp report won't recommend insulation that creates a worse problem six months later.",
              },
            ].map((c) => {
              const Icon = c.icon;
              return (
                <div key={c.title} className="rounded-2xl border bg-card p-5">
                  <div className="rounded-xl bg-accent/10 p-2.5 w-fit text-accent">
                    <Icon className="h-5 w-5" aria-hidden />
                  </div>
                  <h3 className="mt-3 font-semibold">{c.title}</h3>
                  <p className="mt-1.5 text-sm text-muted-foreground">{c.body}</p>
                </div>
              );
            })}
          </div>
        </div>
      </Section>

      <Section className="bg-card">
        <SectionHeader
          eyebrow="The brands"
          title="Meet the Home Check Group"
          description="Each brand has its own specialism, its own surveyors, and its own website. Click through to find out more or book a survey directly."
        />
        <div className="space-y-6">
          {ORDER.map((key) => {
            const brand = CHECK_BRANDS[key];
            const detail = BRAND_DETAIL[key];
            return (
              <article
                key={key}
                className="rounded-2xl border bg-background p-6 md:p-8 shadow-soft"
              >
                <div className="grid md:grid-cols-[auto_1fr_auto] gap-6 items-start">
                  <img
                    src={brand.logo}
                    alt={`${brand.name} logo`}
                    className="h-20 w-20 object-contain shrink-0"
                    loading="lazy"
                  />
                  <div>
                    <p className="text-xs font-semibold tracking-wider uppercase text-accent">
                      {key} · {brand.tagline}
                    </p>
                    <h3 className="mt-1 text-2xl font-bold text-primary">{brand.name}</h3>
                    <p className="mt-3 text-muted-foreground text-pretty">{detail.intro}</p>
                    <div className="mt-5 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
                      <div>
                        <p className="font-semibold mb-1.5">What they survey</p>
                        <ul className="space-y-1 text-muted-foreground">
                          {detail.services.map((s) => (
                            <li key={s} className="flex gap-2">
                              <span className="mt-2 h-1.5 w-1.5 rounded-full bg-accent shrink-0" aria-hidden />
                              <span>{s}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                      <div>
                        <p className="font-semibold mb-1.5">Best for</p>
                        <p className="text-muted-foreground">{detail.bestFor}</p>
                      </div>
                    </div>
                  </div>
                  <a
                    href={brand.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-3 font-semibold hover:bg-primary/90 transition-colors shrink-0 self-start"
                  >
                    Visit {key}
                    <ExternalLink className="h-4 w-4" aria-hidden />
                  </a>
                </div>
              </article>
            );
          })}
        </div>
      </Section>

      <Section>
        <div className="grid lg:grid-cols-[1.1fr_1fr] gap-10 items-start">
          <div>
            <SectionHeader
              eyebrow="Get in touch"
              title="Not sure which Check is right for you?"
              description="Tell us a little about your property and what you've noticed. The team will point you to the right diagnostic brand, or arrange a CRS survey directly if you already know what you need."
            />
            <ul className="mt-2 space-y-3 text-muted-foreground">
              <li className="flex gap-3">
                <span className="mt-2 h-2 w-2 rounded-full bg-accent shrink-0" aria-hidden />
                <span>We'll respond within one working day, usually sooner.</span>
              </li>
              <li className="flex gap-3">
                <span className="mt-2 h-2 w-2 rounded-full bg-accent shrink-0" aria-hidden />
                <span>No high-pressure sales: just clear, honest advice.</span>
              </li>
              <li className="flex gap-3">
                <span className="mt-2 h-2 w-2 rounded-full bg-accent shrink-0" aria-hidden />
                <span>Surveys typically £0-£250 depending on the brand and property.</span>
              </li>
            </ul>
          </div>
          <EnquiryForm
            formType="contact"
            variant="contact"
            sourcePage="/the-home-check-group"
            title="Get in touch"
            subtitle="Tell us about your property and we'll point you to the right brand."
            submitLabel="Send enquiry"
          />
        </div>
      </Section>

      <CTABand
        title="Already know you need the work done?"
        description="Skip the survey and speak to CRS directly about insulation, damp, structural, heating and renewables across Wales and the South West."
      />
    </>
  );
}
